import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./support.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { clearAuthSession, hasValidPlatformAccess } from "../utils/auth";

function calculateHash(obj) {
  const str = JSON.stringify(obj, Object.keys(obj).sort());
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
}

const TKT_PAGE_SIZE = 5;

const TKT_PRIORITY = ["critical", "high", "medium", "low", "feature"];
const TKT_STATUS = ["open", "in_progress", "closed"];

export default function Support() {
  const { t, i18n } = useTranslation();
  const auth = useAuthSession();
  const { token, clientId, isAdmin, language } = auth;
  const [selectedClient, setSelectedClient] = useState(null);
  const [targetClientId, setTargetClientId] = useState(clientId || "");

  const [tktMsgs, setTktMsgs] = useState([]);
  const [editingTkt, setEditingTkt] = useState(null);
  const [tktPriority, setTktPriority] = useState("low");
  const [tktMessage, setTktMessage] = useState("");
  const [tktName, setTktName] = useState("");
  const [tktPhone, setTktPhone] = useState("");
  const [tktStatus, setTktStatus] = useState("open");
  const [tktSubject, setTktSubject] = useState("");
  const [tktResolution, setTktResolution] = useState("");
  const [tktToDelete, setTktToDelete] = useState(null);
  const [deletingTkt, setDeletingTkt] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [tktPage, setTktPage] = useState(0);
  const [validationMessage, setValidationMessage] = useState("");
  const [originalHash, setOriginalHash] = useState(null);
  const [pendingConfirmAction, setPendingConfirmAction] = useState(null);

  const currentLocale = useMemo(() => {
    const lang = (i18n.language || language || "en").toLowerCase();
    return lang.startsWith("es") ? "es-ES" : "en-US";
  }, [i18n.language, language]);

  const handleClientSelect = useCallback(client => {
    setSelectedClient(client);
    setTargetClientId(client.client_id);
  }, []);

  const hasPlatformAccess = useMemo(() => {
    return hasValidPlatformAccess(selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) return;
    if (!hasPlatformAccess) {
      setValidationMessage(t("dashboard.subscriptionRequiredDesc"));
      window.location.href = "/dashboard?tab=onboarding";
    }
  }, [hasPlatformAccess, selectedClient, t]);

  useEffect(() => {
    if (!isAdmin && clientId) {
      setTargetClientId(clientId);
    }
  }, [isAdmin, clientId]);

  const isCreateMode = editingTkt && editingTkt.__mode === "create";
  const isEditMode = editingTkt && editingTkt.ticket_id;

  const paginatedTkts = tktMsgs.slice(
    tktPage * TKT_PAGE_SIZE,
    (tktPage + 1) * TKT_PAGE_SIZE
  );

  const hasNextTktPage = (tktPage + 1) * TKT_PAGE_SIZE < tktMsgs.length;
  const hasPrevTktPage = tktPage > 0;

  useEffect(() => {
    setTktPage(0);
  }, [tktMsgs]);

  function startCreateTkt() {
    if (!targetClientId) {
      setValidationMessage(t("commonLabel.selectClientFirst", "Please select a client first."));
      return;
    }
    setEditingTkt({ __mode: "create" });
    setTktPriority("low");
    setTktMessage("");
    setTktName("");
    setTktPhone("");
    setTktStatus("open");
    setTktSubject("");
    setTktResolution("");
  }

  function cancelEditTkt() {
    setEditingTkt(null);
  }

  async function performSave({ mode = "save" } = {}) {
    const isAdd = mode === "add";

    const body = {
      client_id: targetClientId,
      ticket_id: isAdd ? null : editingTkt?.ticket_id || null,
      customer_name: tktName.trim() || "",
      phone: tktPhone.trim() || "",
      subject: tktSubject.trim() || "",
      message: tktMessage,
      status: isAdd ? "open" : tktStatus,
      priority: tktPriority,
      resolution: isAdd ? "" : tktResolution
    };

    const res = await fetch(`${BACKEND_BASE_URL}/api/tktmsgs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      throw new Error(t("support.failedToSave", "Failed to save ticket"));
    }

    setEditingTkt(null);
    await loadTktMsgs(targetClientId);
  }

  async function saveTkt() {
    if (!targetClientId) return;

    if (!isEditMode) {
      await performSave();
      return;
    }

    const currentSnapshot = buildCurrentSnapshot();
    const newHash = calculateHash(currentSnapshot);

    if (newHash === originalHash) return;

    const onlyStatusOrResolutionChanged =
      currentSnapshot.subject === editingTkt.subject &&
      currentSnapshot.customer_name === editingTkt.customer_name &&
      currentSnapshot.phone === editingTkt.phone &&
      currentSnapshot.message === editingTkt.message &&
      currentSnapshot.priority === editingTkt.priority;

    if (onlyStatusOrResolutionChanged) {
      await performSave();
      return;
    }

    setPendingConfirmAction("save");
    setConfirmOverwrite(true);
  }

  const loadTktMsgs = useCallback(
    async clientId => {
      const res = await fetch(`${BACKEND_BASE_URL}/api/tktmsgs/search`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ client_id: clientId })
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuthSession();
          throw new Error("Invalid or expired token");
        }
        throw new Error(t("support.failedToLoad", "Failed to load ticket list"));
      }

      const data = await res.json();
      setTktMsgs(Array.isArray(data) ? data : []);
    },
    [token, t]
  );

  useEffect(() => {
    if (!token || !targetClientId) return;
    loadTktMsgs(targetClientId).catch(e => setValidationMessage(e.message));
  }, [token, targetClientId, loadTktMsgs]);

  function getTktPriorityLabel(type) {
    return t(`support.priorities.${type}`, type);
  }

  function getTktStatusLabel(state) {
    return t(`support.state.${state}`, state);
  }

  function startEditTkt(tkt) {
    setEditingTkt(tkt);
    setTktPriority(tkt.priority || "low");
    setTktMessage(tkt.message || "");
    setTktName(tkt.customer_name || "");
    setTktPhone(tkt.phone || "");
    setTktStatus(tkt.status || "open");
    setTktSubject(tkt.subject || "");
    setTktResolution(tkt.resolution || "");

    const snapshot = {
      subject: tkt.subject || "",
      customer_name: tkt.customer_name || "",
      phone: tkt.phone || "",
      message: tkt.message || "",
      priority: tkt.priority || "",
      status: tkt.status || "",
      resolution: tkt.resolution || ""
    };

    setOriginalHash(calculateHash(snapshot));
  }

  function buildCurrentSnapshot() {
    return {
      subject: tktSubject.trim(),
      customer_name: tktName.trim(),
      phone: tktPhone.trim(),
      message: tktMessage,
      priority: tktPriority,
      status: tktStatus,
      resolution: tktResolution
    };
  }

  async function confirmDeleteTkt() {
    if (!tktToDelete) return;
    setDeletingTkt(true);

    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/tktmsgs`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ticket_id: tktToDelete.ticket_id,
          client_id: targetClientId
        })
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuthSession();
          throw new Error("Invalid or expired token");
        }
        throw new Error(t("support.deleteFailed", "Delete failed"));
      }

      setTktMsgs(prev => prev.filter(q => q.ticket_id !== tktToDelete.ticket_id));
      setTktToDelete(null);
    } finally {
      setDeletingTkt(false);
    }
  }

  const handleAdd = async () => {
    if (!targetClientId) {
      setValidationMessage(t("commonLabel.selectClientFirst", "Please select a client first."));
      return;
    }

    const currentSnapshot = buildCurrentSnapshot();
    const newHash = calculateHash(currentSnapshot);

    if (isEditMode && newHash !== originalHash) {
      setPendingConfirmAction("add");
      setConfirmOverwrite(true);
      return;
    }

    setTktStatus("open");
    setTktResolution("");
    await performSave({ mode: "add" });
  };

  function formatStatusDate(status, createdAt, updatedAt) {
    let dateSource = status === "open" ? createdAt : (updatedAt || createdAt);
    if (!dateSource) return "";
    return new Date(dateSource).toLocaleDateString(currentLocale, {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  }

  function renderTktForm() {
    return (
      <>
        <div className="tkt-row">
          <label>{t("support.subject")}</label>
          <input
            className="tkt-light-input"
            value={tktSubject}
            onChange={e => setTktSubject(e.target.value)}
          />
        </div>

        <div className="tkt-row">
          <label>{t("support.name")}</label>
          <input
            className="tkt-light-input"
            value={tktName}
            onChange={e => setTktName(e.target.value)}
          />
        </div>

        <div className="tkt-row">
          <label>{t("support.message")}</label>
          <input
            className="tkt-light-input"
            value={tktMessage}
            onChange={e => setTktMessage(e.target.value)}
          />
        </div>

        <div className="tkt-row">
          <label>{t("commonLabel.phone")}</label>
          <input
            className="tkt-light-input"
            value={tktPhone}
            onChange={e => setTktPhone(e.target.value)}
          />
        </div>

        <div className="tkt-row">
          <label>{t("support.priority")}</label>
          <select
            className="tkt-light-input"
            value={tktPriority}
            onChange={e => setTktPriority(e.target.value)}
          >
            {TKT_PRIORITY.map(value => (
              <option key={value} value={value}>
                {t(`support.priorities.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="tkt-row">
          <label>{t("commonLabel.status")}</label>
          <select
            className="tkt-light-input"
            value={tktStatus}
            onChange={e => setTktStatus(e.target.value)}
          >
            {TKT_STATUS.map(value => (
              <option key={value} value={value}>
                {t(`support.state.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="tkt-row">
          <label>{t("support.resolution")}</label>
          <textarea
            className="tkt-light-input tkt-textarea"
            value={tktResolution}
            onChange={e => setTktResolution(e.target.value)}
            rows={3}
          />
        </div>

        <div className="view-actions">
          <button className="btn-mini" onClick={cancelEditTkt}>
            {t("commonLabel.cancel")}
          </button>
          <button className="btn-mini primary" onClick={saveTkt}>
            {t("commonLabel.save")}
          </button>
          <button className="btn-mini primary" onClick={handleAdd}>
            {t("commonLabel.add")}
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="view-page">
      <div className="view-card">
        <div className="view-header">
          <div>
            <h1 className="view-title">{t("support.title")}</h1>
            <p className="view-subtitle">{t("support.subtitle")}</p>
          </div>

          <div className="view-nav">
            <select
              className="view-dropdown"
              defaultValue=""
              onChange={e => {
                const route = e.target.value;
                if (route) window.location.href = route;
              }}
            >
              <option value="" disabled>{t("dropDown.support-page")}</option>
              <option value="/">{t("dropDown.home-page")}</option>
              <option value="/scheduler">{t("dropDown.scheduler-page")}</option>
              <option value="/clients">{t("dropDown.clients-page")}</option>
              <option value="/dashboard">{t("dropDown.dashboard-page")}</option>
              <option value="/crm-lite">{t("dropDown.crm-lite-page")}</option>
              <option value="/qr-code">{t("dropDown.qr-page")}</option>
              <option value="/commerce">{t("dropDown.commerce-page")}</option>
              <option value="/dispatcher">{t("dropDown.dispatcher-page")}</option>
              <option value="/analytics">{t("dropDown.analytics-page")}</option>
              <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
            </select>
          </div>
        </div>

        <ClientSearch onSelect={handleClientSelect} />

        <div className="tkt-tools">
          <div className="tkt-tools-header">
            <div className="tkt-section-title">{t("support.createTkt")}</div>
            <button className="btn-link" onClick={() => startCreateTkt()} title={t("support.newTkt")}>
              {t("support.newTkt")}
            </button>
          </div>

          {isCreateMode && (
            <div className="commonLabel.edit">
              <div className="tkt-edit-title">✏️ {t("support.createTkt")}</div>
              {renderTktForm()}
            </div>
          )}

          <div className="tkt-list">
            {paginatedTkts.map(tkt => {
              const isEditingThis = isEditMode && editingTkt.ticket_id === tkt.ticket_id;

              return (
                <React.Fragment key={tkt.ticket_id}>
                  {isEditingThis ? (
                    <div className="commonLabel.edit">
                      <div className="tkt-edit-title">✏️ {t("support.editTkt")}</div>
                      {renderTktForm()}
                    </div>
                  ) : (
                    <div className="tkt-row">
                      <div className="tkt-item view-card" onClick={() => startEditTkt(tkt)}>
                        <div className="tkt-item-main">
                          <div className="tkt-item-title">
                            {t("support.subject")}: {tkt.subject || "—"}
                          </div>
                          <div className="tkt-item-meta">
                            {t("support.priority")}: {getTktPriorityLabel(tkt.priority) || "TKT"}
                          </div>
                          <div className="tkt-item-meta">
                            {t("support.name")}: {tkt.customer_name || "—"}
                          </div>
                          <div className="tkt-item-meta">
                            {t("commonLabel.status")}: {getTktStatusLabel(tkt.status || "open")} -{" "}
                            {formatStatusDate(tkt.status, tkt.created_at, tkt.updated_at)}
                          </div>
                        </div>
                        <button
                          className="tkt-delete-btn side"
                          onClick={e => {
                            e.stopPropagation();
                            setTktToDelete(tkt);
                          }}
                          title={t("commonLabel.delete")}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {tktMsgs.length > TKT_PAGE_SIZE && (
            <div className="common-pagination">
              {hasPrevTktPage && (
                <button className="btn-link" onClick={() => setTktPage(p => p - 1)}>
                  {t("commonLabel.previous")}
                </button>
              )}
              {hasNextTktPage && (
                <button className="btn-link" onClick={() => setTktPage(p => p + 1)}>
                  {t("commonLabel.next")}
                </button>
              )}
            </div>
          )}
        </div>

        {tktToDelete && (
          <div className="confirm-overlay" onClick={() => setTktToDelete(null)}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("support.confirmDeleteTitle")}</div>
              <div className="confirm-text">
                {t("support.confirmDeleteMessage")} <strong>{tktToDelete.subject || tktToDelete.customer_name}</strong>?
              </div>
              <div className="confirm-actions">
                <button className="btn-mini" onClick={() => setTktToDelete(null)}>
                  {t("commonLabel.cancel")}
                </button>
                <button className="btn-mini primary" disabled={deletingTkt} onClick={confirmDeleteTkt}>
                  {t("commonLabel.delete")}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmOverwrite && (
          <div
            className="confirm-overlay"
            onClick={() => {
              setConfirmOverwrite(false);
              setPendingConfirmAction(null);
            }}
          >
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              {(() => {
                const action = pendingConfirmAction || "save";
                const isAddConfirm = action === "add";
                return (
                  <>
                    <div className="confirm-title">
                      {t(isAddConfirm ? "support.createConfirmTitle" : "support.overwriteSaveTitle")}
                    </div>
                    <div className="confirm-text">
                      {t(isAddConfirm ? "support.createConfirmMessage" : "support.overwriteSaveMessage")}
                    </div>
                    <div className="confirm-actions">
                      <button
                        className="btn-mini"
                        onClick={() => {
                          setConfirmOverwrite(false);
                          setPendingConfirmAction(null);
                        }}
                      >
                        {t("commonLabel.cancel")}
                      </button>
                      <button
                        className="btn-mini primary"
                        onClick={async () => {
                          setConfirmOverwrite(false);
                          setPendingConfirmAction(null);
                          if (action === "add") {
                            setTktStatus("open");
                            setTktResolution("");
                            await performSave({ mode: "add" });
                            return;
                          }
                          await performSave({ mode: "save" });
                        }}
                      >
                        {t(isAddConfirm ? "support.create" : "support.overwrite")}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {validationMessage && (
          <div className="confirm-overlay" onClick={() => setValidationMessage("")}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("support.validationTitle")}</div>
              <div className="confirm-text">{validationMessage}</div>
              <div className="confirm-actions">
                <button
                  className="btn-mini primary"
                  onClick={() => {
                    if (!hasPlatformAccess && selectedClient) {
                      window.location.href = "/dashboard?tab=onboarding";
                    }
                    setValidationMessage("");
                  }}
                >
                  {t("commonLabel.ok")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}